import React, { useState } from 'react';
import {
    Button,
    Text,
    makeStyles,
    Spinner,
} from '@fluentui/react-components';
import { BeakerRegular } from '@fluentui/react-icons';
import Editor from 'react-simple-code-editor';
import { Highlight, themes } from 'prism-react-renderer';
import { useTokenService } from '../../tokenService';
import { CopilotService } from '../../Service/ServerAPI';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import JsonViewer from './JsonViewer';
import { AnalyzeScriptResponse } from '../../types/DebugTypes';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        height: '100%',
    },
    editorSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    editorContainer: {
        backgroundColor: '#3A3E45',
        borderRadius: '4px',
        border: '1px solid #6B7584',
        maxHeight: '400px',
        overflow: 'auto',
    },
    editor: {
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
        fontSize: '13px',
        minHeight: '200px',
        padding: '12px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '8px',
    },
    analyzeButton: {
        backgroundColor: '#0078D4',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#005A9E',
        },
    },
    buttonDisabled: {
        position: 'relative',
    },
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    responseSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
});

const DEFAULT_SCRIPT = `-- Enter your Lua script here
-- Example:
local function isUserIncluded(time, user_id, context)
    return true
end`;

const DebugAnalyze: React.FC<{ dataset?: string }> = ({ dataset }) => {
    const classes = useStyles();
    const [script, setScript] = useState<string>(DEFAULT_SCRIPT);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [response, setResponse] = useState<AnalyzeScriptResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);
    const settings = useSelector((state: RootState) => state.settings);

    const analyzeScript = async () => {
        setIsAnalyzing(true);
        setError(null);

        try {
            const result = await copilotService.analyzeScript(
                script,
                false,
                settings.sessionId,
                settings.apiEndpoint,
                dataset,
                settings.analyticsMode ? "analytics" : "segment"
            );

            setResponse(result);
            setError(null);
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(errorMessage);
            setResponse(null);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const LuaHighlighter = () => (
        <Highlight theme={themes.vsDark} code={script} language="lua">
            {({ tokens, getLineProps, getTokenProps }) => (
                <>
                    {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                            {line.map((token, key) => (
                                <span key={key} {...getTokenProps({ token })} />
                            ))}
                        </div>
                    ))}
                </>
            )}
        </Highlight>
    );

    return (
        <div className={classes.container}>
            <div className={classes.editorSection}>
                <Text weight="semibold">Lua Script</Text>
                <div className={classes.editorContainer}>
                    <Editor
                        value={script}
                        onValueChange={setScript}
                        highlight={LuaHighlighter}
                        padding={12}
                        disabled={isAnalyzing}
                        className={classes.editor}
                        style={{
                            fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
                            fontSize: '13px',
                        }}
                    />
                </div>
            </div>

            <div className={classes.buttonContainer}>
                <Button
                    appearance="primary"
                    icon={isAnalyzing ? undefined : <BeakerRegular />}
                    onClick={analyzeScript}
                    disabled={isAnalyzing || !script.trim()}
                    className={
                        isAnalyzing
                            ? classes.buttonDisabled
                            : classes.analyzeButton
                    }
                >
                    {isAnalyzing ? (
                        <div className={classes.spinnerContainer}>
                            <Spinner size="tiny" />
                            <span>Analyzing...</span>
                        </div>
                    ) : (
                        'Analyze Script'
                    )}
                </Button>
            </div>

            <div className={classes.responseSection}>
                <JsonViewer data={response} error={error} />
            </div>
        </div>
    );
};

export default DebugAnalyze;